import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);
const isDev = import.meta.env?.DEV;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (isDev) {
        console.log('🔐 AuthContext: Checking auth status...');
      }
      try {
        const token = localStorage.getItem('pilotleague_token');
        if (isDev) {
          console.log('🔑 AuthContext: Token exists:', !!token);
        }
        if (token) {
          if (isDev) {
            console.log('📡 AuthContext: Fetching user data via authService.getMe()...');
          }
          const userData = await authService.getMe();
          if (isDev) {
            console.log('✅ AuthContext: User data received:', userData);
          }
          setUser(userData);
        } else {
          if (isDev) {
            console.log('⚠️ AuthContext: No token found');
          }
        }
      } catch (err) {
        if (isDev) {
          console.error('❌ AuthContext: Auth check failed:', err);
        }
        localStorage.removeItem('pilotleague_token');
      } finally {
        if (isDev) {
          console.log('🏁 AuthContext: Auth check complete, isLoading = false');
        }
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = useCallback(async (email, password, rememberMe = false) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password, rememberMe });
      localStorage.setItem('pilotleague_token', response.token);
      if (rememberMe) {
        localStorage.setItem('pilotleague_refresh_token', response.refreshToken);
      }
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Échec de la connexion');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.register(userData);
      localStorage.setItem('pilotleague_token', response.token);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Échec de l\'inscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('pilotleague_token');
      localStorage.removeItem('pilotleague_refresh_token');
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    try {
      // Redirect to Google OAuth
      window.location.href = authService.getGoogleAuthUrl();
    } catch (err) {
      setError(err.message || 'Échec de la connexion Google');
      throw err;
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
    } catch (err) {
      setError(err.message || 'Échec de l\'envoi du lien');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.resetPassword(token, newPassword);
    } catch (err) {
      setError(err.message || 'Échec de la réinitialisation');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    setError(null);
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Échec de la mise à jour');
      throw err;
    }
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getMe();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.deleteAccount();
      localStorage.removeItem('pilotleague_token');
      localStorage.removeItem('pilotleague_refresh_token');
      setUser(null);
    } catch (err) {
      setError(err.message || 'Échec de la suppression');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    loginWithGoogle,
    forgotPassword,
    resetPassword,
    updateProfile,
    refreshUser,
    deleteAccount,
    clearError: () => setError(null),
    // Subscription helpers
    hasSubscription: (tier) => {
      const tiers = ['free', 'premium', 'pro', 'enterprise'];
      const userTierIndex = tiers.indexOf(user?.subscriptionTier || 'free');
      const requiredTierIndex = tiers.indexOf(tier);
      return userTierIndex >= requiredTierIndex;
    },
    isPremium: user?.subscriptionTier === 'premium' || user?.subscriptionTier === 'pro' || user?.subscriptionTier === 'enterprise',
    isPro: user?.subscriptionTier === 'pro' || user?.subscriptionTier === 'enterprise',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
